import { subscribe, getState }    from '../store';
import { socket }                 from '../socket';
import { registerScreen }         from '../router';
import { escHtml, fmtScore }      from '../ui';
import type { RoundScore, Player } from '../../../shared/types';

// ─── Constants ────────────────────────────────────────────────────────────────

const NEXT_DELAY_SECS = 3;

// ─── Volcano SVG renderer ─────────────────────────────────────────────────────
// Side-view cross-section. altitude 100 = top, 0 = bottom.
// SVG viewport: 375 × 240

const SVG_W      = 375;
const SVG_H      = 240;
const PEAK_Y     = 20;   // y coordinate of volcano peak
const BASE_Y     = 220;  // y coordinate of volcano base

function altitudeToY(altitude: number): number {
  // altitude 100 → PEAK_Y, altitude 0 → BASE_Y
  return PEAK_Y + ((100 - altitude) / 100) * (BASE_Y - PEAK_Y);
}

// x position for a character slot (spread across volcano width at that altitude)
function altitudeToX(altitude: number, slot: number, total: number): number {
  const volcanoWidthAtY = (1 - altitude / 100) * (SVG_W * 0.6) + 20;
  const center = SVG_W / 2;
  const step   = Math.min(volcanoWidthAtY / (total + 1), 32);
  return center + (slot - (total - 1) / 2) * step;
}

function buildVolcanoSvg(players: Player[], lavaLevel: number): string {
  const lavaY    = altitudeToY(lavaLevel);
  const alivePlayers = players.filter(p => p.status !== 'ghost');
  const ghosts       = players.filter(p => p.status === 'ghost');

  // Player emoji avatars
  const EMOJIS = ['🟠','🔵','🟢','🟣','🟡','🔴','⚫','🟤'];

  const charSvg = (p: Player, idx: number, list: Player[], isGhost: boolean): string => {
    const y   = altitudeToY(p.altitude);
    const x   = altitudeToX(p.altitude, idx, list.length);
    const em  = EMOJIS[p.joinOrder % EMOJIS.length];
    const cls = isGhost ? 'volcano-char ghost' : 'volcano-char';
    return `
      <g class="${cls}" id="char-${p.id}"
         transform="translate(${x}, ${y})"
         data-altitude="${p.altitude}">
        <text dy="-2">${isGhost ? '💀' : em}</text>
        <text dy="12" style="font-size:8px;fill:#999">${escHtml(p.username.slice(0, 6))}</text>
      </g>
    `;
  };

  return `
    <svg class="results__volcano-svg" viewBox="0 0 ${SVG_W} ${SVG_H}" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id="vg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#3a3a3a"/>
          <stop offset="100%" stop-color="#1a1a1a"/>
        </linearGradient>
        <linearGradient id="lava-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#FF6B35" stop-opacity="0.9"/>
          <stop offset="100%" stop-color="#E63946" stop-opacity="1"/>
        </linearGradient>
        <filter id="lava-glow">
          <feGaussianBlur stdDeviation="3" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      <!-- Volcano body -->
      <polygon
        points="${SVG_W / 2},${PEAK_Y} ${SVG_W - 10},${BASE_Y} 10,${BASE_Y}"
        fill="url(#vg)"
      />

      <!-- Lava -->
      <rect
        id="lava-rect"
        x="0" y="${lavaY}"
        width="${SVG_W}" height="${SVG_H - lavaY + 10}"
        fill="url(#lava-grad)"
        filter="url(#lava-glow)"
      />
      <!-- Lava glow line -->
      <line
        x1="0" y1="${lavaY}" x2="${SVG_W}" y2="${lavaY}"
        stroke="#FF6B35" stroke-width="2" opacity="0.8"
      />

      <!-- Ghost players (below lava visually) -->
      ${ghosts.map((p, i) => charSvg(p, i, ghosts, true)).join('')}

      <!-- Alive players (above lava) -->
      ${alivePlayers.map((p, i) => charSvg(p, i, alivePlayers, false)).join('')}

      <!-- Crater glow -->
      <ellipse cx="${SVG_W / 2}" cy="${PEAK_Y}" rx="18" ry="7"
        fill="#E63946" opacity="0.7" class="crater-anim"/>
    </svg>
  `;
}

// Animate characters descending after scores render
function animateDescentChars(players: Player[]): void {
  setTimeout(() => {
    for (const p of players) {
      const el = document.getElementById(`char-${p.id}`);
      if (!el) continue;
      const y  = altitudeToY(p.altitude);
      const x  = altitudeToX(
        p.altitude,
        p.joinOrder,
        players.length
      );
      el.setAttribute('transform', `translate(${x}, ${y})`);
    }
    // Also animate lava up
    const lavaRect = document.getElementById('lava-rect');
    const { session } = getState();
    if (lavaRect && session) {
      const newY = altitudeToY(session.volcano.lavaLevel);
      lavaRect.setAttribute('y', String(newY));
      lavaRect.setAttribute('height', String(SVG_H - newY + 10));
    }
  }, 600); // slight delay so user sees the screen first
}

// ─── Score list ───────────────────────────────────────────────────────────────

function buildScoreList(scores: RoundScore[], myPlayerId: string | null): string {
  const alive  = scores.filter(s => s.status !== 'ghost');
  const ghosts = scores.filter(s => s.status === 'ghost');

  const rowHtml = (s: RoundScore, rank: number | string): string => {
    const isMe    = s.playerId === myPlayerId;
    const isGhost = s.status === 'ghost';
    const delta   = s.delta > 0 ? `+${fmtScore(s.delta)}` : '—';

    return `
      <div class="results__row ${isGhost ? 'results__row--ghost' : ''} ${isMe ? 'results__row--me' : ''}">
        <span class="results__rank">${rank}</span>
        <span class="results__name">
          ${isGhost ? '💀 ' : s.status === 'rescued' ? '🪙 ' : ''}${escHtml(s.username)}
        </span>
        <span class="results__delta ${s.delta === 0 ? 'results__delta--zero' : ''}">${delta}</span>
        <span class="results__total">${fmtScore(s.score)}</span>
      </div>
    `;
  };

  const aliveRows  = alive.map((s, i)  => rowHtml(s, i + 1)).join('');
  const ghostRows  = ghosts.map(s      => rowHtml(s, '💀')).join('');
  const divider    = ghosts.length > 0
    ? `<div class="results__divider">Caught by lava</div>`
    : '';

  return aliveRows + divider + ghostRows;
}

// ─── Countdown button ─────────────────────────────────────────────────────────

function startNextCountdown(btn: HTMLButtonElement, isHost: boolean): void {
  if (!isHost) {
    btn.disabled    = true;
    btn.textContent = 'Waiting for host…';
    return;
  }

  let secs = NEXT_DELAY_SECS;
  btn.disabled = true;
  btn.classList.add('btn--countdown');
  btn.textContent = `Next Game (${secs})`;

  const tick = setInterval(() => {
    secs--;
    if (secs <= 0) {
      clearInterval(tick);
      btn.disabled    = false;
      btn.textContent = 'Next Game ▶';
      btn.classList.add('ticking');
    } else {
      btn.textContent = `Next Game (${secs})`;
    }
  }, 1000);
}

// ─── Screen ───────────────────────────────────────────────────────────────────

let unsubscribe: (() => void) | null = null;
let msgUnsub:    (() => void) | null = null;

registerScreen('results', {
  mount() {
    const screen = document.getElementById('screen-results')!;
    const { session, myPlayer, playerId } = getState();
    if (!session) return;

    const players     = Object.values(session.players);
    const lavaLevel   = session.volcano.lavaLevel;

    // Build initial scores from current session state
    const scores: RoundScore[] = players
      .sort((a, b) => b.sessionScore - a.sessionScore)
      .map((p, i) => ({
        playerId: p.id,
        username: p.username,
        score:    p.status === 'ghost' ? p.frozenScore : p.sessionScore,
        delta:    0, // will be updated when server sends round_results
        status:   p.status,
        rank:     i + 1,
      }));

    screen.innerHTML = `
      <!-- Volcano visualization -->
      <div class="results__volcano">
        ${buildVolcanoSvg(players, lavaLevel)}
        <div class="results__gauge">
          <div class="results__gauge__fill" id="gauge-fill"
               style="height: ${100 - lavaLevel}%"></div>
        </div>
        <div class="results__round-badge">
          Round ${session.currentRound} / ${session.totalRounds}
        </div>
      </div>

      <!-- Scores -->
      <div class="results__scores">
        <h2 class="results__title">Round Results</h2>
        <div class="results__list" id="results-list">
          ${buildScoreList(scores, playerId)}
        </div>
      </div>

      <!-- Footer -->
      <div class="results__footer">
        <button class="btn btn--primary btn--countdown" id="btn-next-game" disabled>
          Next Game ▶
        </button>
      </div>
    `;

    // Animate descent
    animateDescentChars(players);

    // Countdown
    const nextBtn = document.getElementById('btn-next-game') as HTMLButtonElement;
    startNextCountdown(nextBtn, !!myPlayer?.isHost);

    nextBtn.addEventListener('click', () => {
      socket.send({ type: 'host_next' });
      nextBtn.disabled    = true;
      nextBtn.textContent = 'Loading…';
    });

    // Update scores when server sends round_results
    msgUnsub = socket.onMessage(msg => {
      if (msg.type !== 'round_results') return;
      const list = document.getElementById('results-list');
      if (list) list.innerHTML = buildScoreList(msg.scores, playerId ?? null);
    });

    // Update volcano gauge on volcano_update
    unsubscribe = subscribe(({ session: s }) => {
      if (!s) return;
      const fill = document.getElementById('gauge-fill');
      if (fill) fill.style.height = `${100 - s.volcano.lavaLevel}%`;
    });
  },

  unmount() {
    unsubscribe?.();
    msgUnsub?.();
    unsubscribe = null;
    msgUnsub    = null;
  },
});
